import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicCourse, PublicClassroom, CourseMethod } from '@/types/course';
import { useAuthStore } from '@/store/authStore';
import { useRegistrationStore } from '@/store/registrationStore';
import { CourseApi } from '@/services/courseApi';
import { RegistrationApi, PromotionInfo, Registration, PaymentInfo, CourseRegistrationRequest } from '@/services/registrationApi';
import { formatPriceSimple } from '@/utils/courseFormat';
import { formatDbDateVi, isClassroomOpenForEnrollment, isClassroomPastStartDate } from '@/utils/dbDateDisplay';
import {
  AcademicCapIcon,
  ClockIcon,
  CalendarDaysIcon,
  MapPinIcon,
  ComputerDesktopIcon,
  ExclamationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import PaymentQRDisplay from './PaymentQRDisplay';
import CompanyInvoiceGuidance from './CompanyInvoiceGuidance';
import type { GiftOfferAsSource } from '@/components/course/CourseGiftPromoBanner';

interface CourseRegistrationFormProps {
  course: PublicCourse;
  onClose: () => void;
  /** Gọi sau khi tạo đăng ký, khi form đang ở bước QR — không được unmount form ở đây. */
  onSuccess?: () => void;
}

const CourseRegistrationForm: React.FC<CourseRegistrationFormProps> = ({
  course,
  onClose,
  onSuccess
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, patchUser } = useAuthStore();
  const { 
    createRegistration, 
    isCreatingRegistration, 
    registrationError, 
    currentRegistration,
    clearRegistrationError 
  } = useRegistrationStore();

  const [classrooms, setClassrooms] = useState<PublicClassroom[]>([]);
  const [selectedClassroom, setSelectedClassroom] = useState<PublicClassroom | null>(null);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);
  const [classroomError, setClassroomError] = useState<string | null>(null);
  const [step, setStep] = useState<'auth-check' | 'classroom-selection' | 'confirmation' | 'payment'>('auth-check');
  const [showBackButton, setShowBackButton] = useState(false);
  const [promotion, setPromotion] = useState<PromotionInfo | null>(null);
  const [loadingPromotion, setLoadingPromotion] = useState(false);
  /** none = Bán cho người tiêu dùng; personal = HĐ cá nhân; company = hotline */
  const [invoiceType, setInvoiceType] = useState<'none' | 'personal' | 'company'>('none');
  const [studentFullName, setStudentFullName] = useState('');
  const [studentEmail, setStudentEmail] = useState('');
  const [invoiceCccd, setInvoiceCccd] = useState('');
  const [invoiceAddress, setInvoiceAddress] = useState('');
  const [invoiceFieldError, setInvoiceFieldError] = useState<string | null>(null);
  const [giftAsSource, setGiftAsSource] = useState<GiftOfferAsSource[]>([]);
  /** KM tặng khóa ưu tiên — không áp / không hiện KM giảm giá khác */
  const hasGiftPromo = giftAsSource.length > 0;
  const showDiscountPromo =
    !hasGiftPromo && !!promotion && promotion.discountAmount > 0;

  // Prefill thông tin học viên từ user
  useEffect(() => {
    if (user?.fullName) {
      setStudentFullName((prev) => (prev ? prev : user.fullName));
    }
    if (user?.email) {
      setStudentEmail((prev) => (prev ? prev : user.email));
    }
    if (user?.cccd) {
      setInvoiceCccd((prev) => (prev ? prev : user.cccd || ''));
    }
    if (user?.address) {
      setInvoiceAddress((prev) => (prev ? prev : user.address || ''));
    }
  }, [user?.fullName, user?.email, user?.cccd, user?.address]);

  useEffect(() => {
    let cancelled = false;
    const key = course.hashId || String(course.id);
    CourseApi.getGiftOffers(key)
      .then((offers) => {
        if (!cancelled) setGiftAsSource(offers.asSource || []);
      })
      .catch(() => {
        if (!cancelled) setGiftAsSource([]);
      });
    return () => {
      cancelled = true;
    };
  }, [course.id, course.hashId]);

  // Clear error state on component mount and course change
  useEffect(() => {
    // Clear any previous registration errors when opening modal for a new course
    clearRegistrationError();
    setClassroomError(null);
    setInvoiceFieldError(null);
  }, [course.id, clearRegistrationError]);

  // Check authentication status on mount
  useEffect(() => {
    if (!isAuthenticated) {
      // Redirect to login with return URL
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      navigate(`/login?returnUrl=${returnUrl}`);
      return;
    }
    
    setStep('classroom-selection');
    loadPromotionInfo(); // Load promotion once for the course
    loadClassrooms();
  }, [isAuthenticated, navigate]);

  // Load promotion info for the course
  const loadPromotionInfo = async () => {
    try {
      setLoadingPromotion(true);
      const promotionInfo = await RegistrationApi.getBestPromotion(course.id);
      setPromotion(promotionInfo);
    } catch (error) {
      console.error('Error loading promotion info:', error);
      // Set default promotion info if failed
      setPromotion({
        promotionId: null,
        promotion_user_Id: null,
        discountAmount: 0,
        fee: course.fee,
        orgFee: course.fee
      });
    } finally {
      setLoadingPromotion(false);
    }
  };

  // Load classrooms for the course
  const loadClassrooms = async () => {
    try {
      setLoadingClassrooms(true);
      setClassroomError(null);
      const courseIdAsString = `${course.id}`
      const courseClassrooms = await CourseApi.getCourseClassrooms(courseIdAsString);
      
      // Check if this is an interactive course that needs upcoming class filtering
      const isInteractiveCourse = course.method === 'ONLINE' || 
                                 course.method === 'OFFLINE' || 
                                 course.method === 'INTERACTIVE_ONLINE' ||
                                 course.method === 'INTERACTIVE_OFFLINE' ||
                                 course.method === CourseMethod.ONLINE ||
                                 course.method === CourseMethod.OFFLINE;
      
      // Handle different course types
      if (course.method === 'VIDEO' || course.method === CourseMethod.VIDEO) {
        // Video courses: Don't filter by date, get first classroom and go to confirmation
        setClassrooms(courseClassrooms);
        if (courseClassrooms.length > 0) {
          setSelectedClassroom(courseClassrooms[0]);
          setShowBackButton(false); // No back button for VIDEO courses
          setStep('confirmation');
        } else {
          setClassroomError('Không có lớp học nào khả dụng cho khóa học này');
        }
      } else if (isInteractiveCourse) {
        // Interactive: còn mở ĐK nếu ngày KG >= hôm nay (theo lịch VN)
        const upcomingClassrooms = courseClassrooms.filter(classroom =>
          isClassroomOpenForEnrollment(classroom.startDate)
        );
        
        setClassrooms(upcomingClassrooms);
        
        if (upcomingClassrooms.length === 0) {
          setClassroomError('Không có lớp học nào sắp diễn ra');
        } else if (upcomingClassrooms.length === 1) {
          // Only one upcoming class: Skip selection and go directly to confirmation
          setSelectedClassroom(upcomingClassrooms[0]);
          setShowBackButton(false); // No back button when skipping selection
          setStep('confirmation');
        } else {
          // Multiple upcoming classes: Show selection screen
          setShowBackButton(true); // Will show back button after selection
          setStep('classroom-selection');
        }
      }
    } catch (error) {
      console.error('Error loading classrooms:', error);
      setClassroomError(error instanceof Error ? error.message : 'Không thể tải thông tin lớp học');
    } finally {
      setLoadingClassrooms(false);
    }
  };

  // Handle classroom selection
  const handleClassroomSelect = (classroom: PublicClassroom) => {
    // Clear any previous errors when selecting a new classroom
    clearRegistrationError();
    setSelectedClassroom(classroom);
    // Back button is already set to true when multiple classes are shown
    setStep('confirmation');
  };

  // Handle registration submission
  const handleSubmitRegistration = async () => {
    if (!selectedClassroom) return;
    if (invoiceType === 'company') return;

    const name = studentFullName.trim();
    const email = studentEmail.trim();

    if (invoiceType === 'personal') {
      const cccd = invoiceCccd.trim();
      const address = invoiceAddress.trim();
      if (!name) {
        setInvoiceFieldError('Vui lòng nhập họ tên');
        return;
      }
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setInvoiceFieldError('Vui lòng nhập email hợp lệ');
        return;
      }
      if (!/^\d{12}$/.test(cccd)) {
        setInvoiceFieldError('CCCD phải gồm đúng 12 chữ số');
        return;
      }
      if (!address) {
        setInvoiceFieldError('Vui lòng nhập địa chỉ để xuất hóa đơn');
        return;
      }
      setInvoiceFieldError(null);
    }

    try {
      clearRegistrationError();

      const registrationRequest: CourseRegistrationRequest = {
        classroomId: selectedClassroom.id,
        invoiceType
      };

      if (invoiceType === 'personal') {
        registrationRequest.invoiceFullName = name;
        registrationRequest.invoiceEmail = email;
        registrationRequest.invoiceCccd = invoiceCccd.trim();
        registrationRequest.invoiceAddress = invoiceAddress.trim();
      }

      if (showDiscountPromo && promotion?.promotionId) {
        registrationRequest.promotion_id = promotion.promotionId;
      }
      if (showDiscountPromo && promotion?.promotion_user_Id) {
        registrationRequest.promotion_user_id = promotion.promotion_user_Id;
      }

      const result = await createRegistration(registrationRequest);

      if (result) {
        if (invoiceType === 'personal') {
          const parts = name.split(/\s+/);
          patchUser({
            fullName: name,
            firstName: parts[parts.length - 1] || name,
            email,
            cccd: invoiceCccd.trim(),
            address: invoiceAddress.trim()
          });
        }
        setStep('payment');
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error) {
      console.error('Registration error:', error);

      if (error instanceof Error && error.message.includes('Authentication required')) {
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/login?returnUrl=${returnUrl}`);
        return;
      }
    }
  };

  // Đã qua ngày khai giảng (theo lịch VN)
  const isClassroomExpired = (classroom: PublicClassroom) =>
    isClassroomPastStartDate(classroom.startDate);

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-m2-surface-container rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <ExclamationCircleIcon className="h-12 w-12 text-orange-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-m2-on-surface mb-2">Yêu cầu đăng nhập</h3>
            <p className="text-m2-on-surface-variant mb-4">
              Bạn cần đăng nhập để đăng ký khóa học này.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={onClose}
                className="px-4 py-2 text-m2-on-surface-variant border border-m2-outline rounded-md hover:bg-m2-surface-low"
              >
                Hủy
              </button>
              <button
                onClick={() => {
                  const returnUrl = encodeURIComponent(window.location.pathname);
                  navigate(`/login?returnUrl=${returnUrl}`);
                }}
                className="px-4 py-2 bg-m2-primary text-m2-on-primary rounded-md hover:bg-eh-forest-shadow"
              >
                Đăng nhập
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const invoiceChoiceHeader =
    step === 'confirmation' && selectedClassroom ? (
      <div
        className="mx-3 mb-3 sm:mx-5 sm:mb-4 rounded-lg border border-slate-200 bg-white px-3 py-3 sm:px-4 sm:py-3 space-y-3 shadow-sm"
        role="region"
        aria-label="Thông tin học viên và hóa đơn"
      >
        {/* Thông tin học viên — luôn hiện phía trên */}
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide text-teal-700 mb-2">
            Thông tin học viên
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="studentFullName" className="block text-xs font-medium text-slate-600 mb-1">
                Họ tên
              </label>
              <input
                id="studentFullName"
                type="text"
                value={studentFullName}
                onChange={(e) => {
                  setStudentFullName(e.target.value);
                  setInvoiceFieldError(null);
                }}
                className="registration-light-input w-full rounded-md border border-slate-300 !bg-white px-3 py-2 text-sm !text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Họ và tên"
                autoComplete="name"
              />
            </div>
            <div>
              <label htmlFor="studentEmail" className="block text-xs font-medium text-slate-600 mb-1">
                Email
              </label>
              <input
                id="studentEmail"
                type="email"
                value={studentEmail}
                onChange={(e) => {
                  setStudentEmail(e.target.value);
                  setInvoiceFieldError(null);
                }}
                className="registration-light-input w-full rounded-md border border-slate-300 !bg-white px-3 py-2 text-sm !text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="email@example.com"
                autoComplete="email"
              />
            </div>
          </div>
        </div>

        {/* Lựa chọn hóa đơn */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4 pt-3 border-t border-slate-200">
          <div className="flex items-start gap-2 min-w-0">
            <span className="mt-1.5 inline-flex h-2 w-2 rounded-full bg-teal-600 shrink-0" aria-hidden />
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wide text-teal-700">Hóa đơn</p>
              <p className="text-sm sm:text-base font-semibold text-slate-900 leading-snug">
                Bạn có cần xuất hóa đơn không?
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 shrink-0">
            {(
              [
                { value: 'none' as const, label: 'Không' },
                { value: 'personal' as const, label: 'Hóa đơn cá nhân' },
                { value: 'company' as const, label: 'Hóa đơn công ty' }
              ] as const
            ).map((opt) => (
              <label
                key={opt.value}
                className={`flex items-center gap-2 cursor-pointer rounded-md px-2.5 py-1.5 border transition-colors ${
                  invoiceType === opt.value
                    ? 'border-teal-600 bg-teal-50 text-teal-900'
                    : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                }`}
              >
                <input
                  type="radio"
                  name="invoiceType"
                  checked={invoiceType === opt.value}
                  onChange={() => {
                    setInvoiceType(opt.value);
                    setInvoiceFieldError(null);
                  }}
                  className="text-teal-600 border-slate-300 focus:ring-teal-500"
                />
                <span className="text-sm font-semibold whitespace-nowrap">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {invoiceType === 'personal' && (
          <div className="space-y-3">
            <div>
              <label htmlFor="invoiceCccd" className="block text-xs font-medium text-slate-600 mb-1">
                CCCD <span className="text-red-600">*</span>
              </label>
              <input
                id="invoiceCccd"
                type="text"
                inputMode="numeric"
                maxLength={12}
                value={invoiceCccd}
                onChange={(e) => {
                  setInvoiceCccd(e.target.value.replace(/\D/g, '').slice(0, 12));
                  setInvoiceFieldError(null);
                }}
                className="registration-light-input w-full sm:max-w-xs rounded-md border border-slate-300 !bg-white px-3 py-2 text-sm !text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="12 chữ số"
                autoComplete="off"
              />
            </div>
            <div>
              <label htmlFor="invoiceAddress" className="block text-xs font-medium text-slate-600 mb-1">
                Địa chỉ <span className="text-red-600">*</span>
              </label>
              <input
                id="invoiceAddress"
                type="text"
                value={invoiceAddress}
                onChange={(e) => {
                  setInvoiceAddress(e.target.value);
                  setInvoiceFieldError(null);
                }}
                className="registration-light-input w-full rounded-md border border-slate-300 !bg-white px-3 py-2 text-sm !text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Địa chỉ liên hệ / thường trú để xuất hóa đơn"
                autoComplete="street-address"
              />
            </div>
          </div>
        )}

        {invoiceFieldError && (
          <p className="text-sm text-red-600">{invoiceFieldError}</p>
        )}

        {invoiceType === 'company' && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs sm:text-sm text-amber-950 leading-snug">
            Xuất hóa đơn cho công ty <strong>không đăng ký qua ứng dụng</strong>. Vui lòng{' '}
            <strong>liên hệ 0983213076</strong> để được hướng dẫn.
          </div>
        )}
      </div>
    ) : null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
      <div
        className="bg-slate-50 text-slate-900 rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden border-2 border-slate-200 shadow-2xl
          [&_.text-m2-on-surface]:!text-slate-900
          [&_.text-m2-on-surface-variant]:!text-slate-600
          [&_.bg-m2-surface-container]:!bg-white
          [&_.bg-m2-surface-low]:!bg-white
          [&_.bg-m2-surface-high]:!bg-slate-100
          [&_.border-m2-outline]:!border-slate-300
          [&_.border-m2-outline-variant]:!border-slate-200
          [&_.hover\:bg-m2-surface-low:hover]:!bg-slate-100
          [&_.hover\:bg-m2-surface-high:hover]:!bg-slate-100
          [&_.hover\:bg-m2-primary\/10:hover]:!bg-teal-50
          [&_.hover\:border-m2-primary\/40:hover]:!border-teal-400
          [&_.text-m2-primary]:!text-teal-700
          [&_.text-m2-secondary]:!text-emerald-700
          [&_.bg-m2-primary]:!bg-teal-600
          [&_.text-m2-on-primary]:!text-white
          [&_.hover\:bg-m2-primary-container:hover]:!bg-teal-700
          [&_.text-m2-outline]:!text-slate-400"
      >
        {/* Header + thông tin HV / hóa đơn */}
        <header className="shrink-0 border-b border-slate-200 bg-white z-10">
          <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 truncate">Đăng ký khóa học</h2>
              <p className="text-xs sm:text-sm text-slate-500 truncate">{course.name}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 text-slate-400 hover:text-slate-700 p-1 rounded-md hover:bg-slate-100"
              aria-label="Đóng"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          {invoiceChoiceHeader}
        </header>

        {/* Content (cuộn độc lập, header + Hóa đơn luôn nhìn thấy) */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 sm:p-4 bg-slate-50">
          {step === 'classroom-selection' && (
            <div>
              <div className="mb-2">
                <h3 className="text-lg font-medium text-m2-on-surface mb-2">Chọn lịch học</h3>
                <p className="text-m2-on-surface-variant">
                  Vui lòng chọn lịch học phù hợp với bạn
                </p>
              </div>

              {loadingClassrooms ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : classroomError ? (
                <div className="text-center py-2">
                  <ExclamationCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <p className="text-red-600">{classroomError}</p>
                  <button
                    onClick={loadClassrooms}
                    className="mt-4 px-4 py-2 bg-m2-primary text-m2-on-primary rounded-md hover:bg-eh-forest-shadow"
                  >
                    Thử lại
                  </button>
                </div>
              ) : classrooms.length === 0 ? (
                <div className="text-center py-8">
                  <ExclamationCircleIcon className="h-12 w-12 text-m2-outline mx-auto mb-4" />
                  <p className="text-m2-on-surface-variant">Hiện tại không có lớp học nào khả dụng</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {classrooms.map((classroom) => {
                    const isExpired = isClassroomExpired(classroom);
                    const isUnavailable = isExpired;

                    return (
                      <div
                        key={classroom.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                          isUnavailable
                            ? 'border-m2-outline-variant bg-m2-surface-low cursor-not-allowed'
                            : 'border-m2-outline-variant hover:border-m2-primary/40 hover:bg-m2-primary/10'
                        }`}
                        onClick={() => !isUnavailable && handleClassroomSelect(classroom)}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <div className="flex justify-between items-center">
                              <h4 className="font-medium text-m2-on-surface">{classroom.name}</h4>
                              <div className="text-right">
                                {loadingPromotion && !hasGiftPromo ? (
                                  <div className="text-sm text-m2-on-surface-variant">Loading...</div>
                                ) : showDiscountPromo && promotion ? (
                                  <div>
                                    <div className="text-sm text-m2-on-surface-variant line-through">
                                      {formatPriceSimple(promotion.orgFee)}
                                    </div>
                                    <div className="font-semibold text-m2-primary">
                                      {formatPriceSimple(promotion.fee)}
                                    </div>
                                    <div className="text-xs text-m2-secondary">
                                      Giảm {formatPriceSimple(promotion.discountAmount)}
                                    </div>
                                  </div>
                                ) : classroom.fee ? (
                                  <span className="font-semibold text-m2-primary">
                                    {formatPriceSimple(classroom.fee)}
                                  </span>
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {isExpired && (
                            <span className="text-sm font-medium text-red-600 ml-2">
                              Đã bắt đầu
                            </span>
                          )}
                        </div>

                        <div className="space-y-2 text-sm text-m2-on-surface-variant">
                          {classroom.startDate && (
                            <div className="flex items-center gap-2">
                              <CalendarDaysIcon className="h-4 w-4" />
                              <span>
                                Bắt đầu: {formatDbDateVi(classroom.startDate)}
                              </span>
                            </div>
                          )}
                          {(classroom.studyTime || classroom.schedule) && (
                            <div className="flex items-start gap-2">
                              <ClockIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                              <div>
                                <div className="font-medium text-m2-on-surface mb-1">Lịch học:</div>
                                <div className="whitespace-pre-line">
                                  {classroom.studyTime && classroom.schedule ? (
                                    `${classroom.studyTime} - ${classroom.schedule}`
                                  ) : classroom.studyTime ? (
                                    classroom.studyTime
                                  ) : (
                                    classroom.schedule
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                          {classroom.address && (
                            <div className="flex items-start gap-2">
                              <MapPinIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
                              <div>
                                <div className="font-medium text-m2-on-surface mb-1">Địa điểm học:</div>
                                <div>{classroom.address}</div>
                              </div>
                            </div>
                          )}
                          {classroom.googleMeetLink && (
                            <div className="flex items-center gap-2">
                              <ComputerDesktopIcon className="h-4 w-4" />
                              <span>Học trực tuyến qua Google Meet</span>
                            </div>
                          )}
                        </div>

                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {step === 'confirmation' && selectedClassroom && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-medium text-m2-on-surface mb-4">
                  {(course.method === 'VIDEO' || course.method === CourseMethod.VIDEO) ? 'Xác nhận đăng ký khóa học Video' : 'Xác nhận đăng ký'}
                </h3>

                {/* Course Info */}
                <div className="bg-m2-surface-low rounded-lg p-4 mb-4 border border-m2-outline-variant">
                  <div className="flex items-start gap-4">
                    {course.imageUrl && (
                      <img
                        src={course.imageUrl}
                        alt={course.name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <h4 className="font-medium text-m2-on-surface mb-1">{course.name}</h4>
                      <p className="text-sm text-m2-on-surface-variant mb-2">
                        {String(course.lecturer || '').replace(/<[^>]*>/g, '').trim()}
                      </p>
                      
                      {/* Learning format */}
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-m2-on-surface-variant">Hình thức học:</span>
                        <span className="text-sm font-medium text-m2-on-surface">
                          {(course.method === 'VIDEO' || course.method === CourseMethod.VIDEO)
                            ? 'Video tự học' 
                            : (course.method === 'ONLINE' || course.method === CourseMethod.ONLINE)
                            ? 'Trực tuyến' 
                            : (course.method === 'OFFLINE' || course.method === CourseMethod.OFFLINE)
                            ? 'Tại trung tâm' 
                            : 'Kết hợp'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-2 text-sm text-m2-on-surface-variant">
                        <div className="flex items-center gap-1">
                          <ClockIcon className="h-4 w-4" />
                          <span>{course.timeInHour} giờ</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <AcademicCapIcon className="h-4 w-4" />
                          <span>{course.timeInLesson} bài học</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment summary — clear lines: course fees + gift + total */}
                {(() => {
                  const payFee =
                    showDiscountPromo && promotion
                      ? promotion.fee
                      : Number(selectedClassroom.fee || course.fee || 0)
                  const gift = giftAsSource[0]
                  const giftFee = gift?.giftCourseFee ?? 0
                  return (
                    <div className="mb-4 rounded-lg border border-m2-outline-variant bg-white p-4 space-y-2.5 text-sm">
                      <p className="text-xs font-semibold uppercase tracking-wide text-m2-on-surface-variant">
                        Tóm tắt thanh toán
                      </p>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="min-w-0 text-m2-on-surface">
                          {course.name}
                        </span>
                        <span className="shrink-0 font-semibold text-m2-on-surface tabular-nums">
                          {loadingPromotion && !hasGiftPromo
                            ? '...'
                            : formatPriceSimple(payFee)}
                        </span>
                      </div>
                      {hasGiftPromo && gift ? (
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="min-w-0 text-m2-on-surface">
                            <span className="text-m2-error font-medium">Tặng kèm:</span>{' '}
                            {gift.giftCourseName}
                          </span>
                          <span className="shrink-0 tabular-nums">
                            <span className="text-m2-on-surface-variant line-through mr-1.5">
                              {formatPriceSimple(giftFee)}
                            </span>
                            <span className="font-bold text-m2-error">0 đ</span>
                          </span>
                        </div>
                      ) : null}
                      <div className="flex items-baseline justify-between gap-3 border-t border-m2-outline-variant pt-2.5">
                        <span className="font-semibold text-m2-on-surface">Tổng thanh toán</span>
                        <span className="font-bold text-lg text-eh-forest-ink tabular-nums">
                          {formatPriceSimple(payFee)}
                        </span>
                      </div>
                      {hasGiftPromo ? (
                        <p className="text-xs text-m2-on-surface-variant leading-relaxed">
                          Hóa đơn (nếu xuất) chỉ cho khóa chính — không xuất HĐ khóa tặng 0đ.
                        </p>
                      ) : null}
                    </div>
                  )
                })()}

                {/* Detailed information for ONLINE/OFFLINE courses only */}
                {course.method !== 'VIDEO' && course.method !== CourseMethod.VIDEO && (
                  <div className="border rounded-lg p-4 mb-4">
                    <h5 className="font-medium text-m2-on-surface mb-3">Thông tin lớp học</h5>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-m2-on-surface-variant">Ngày bắt đầu:</span>
                        <span className="font-medium">
                          {selectedClassroom.startDate 
                            ? formatDbDateVi(selectedClassroom.startDate)
                            : 'Chưa xác định'}
                        </span>
                      </div>
                      
                      {(selectedClassroom.studyTime || selectedClassroom.schedule) && (
                        <div className="flex justify-between items-start">
                          <span className="text-m2-on-surface-variant">Lịch học:</span>
                          <div className="font-medium text-right max-w-sm">
                            {selectedClassroom.studyTime && selectedClassroom.schedule ? (
                              <div>{selectedClassroom.studyTime} - {selectedClassroom.schedule}</div>
                            ) : selectedClassroom.studyTime ? (
                              <div>{selectedClassroom.studyTime}</div>
                            ) : (
                              <div className="text-sm text-m2-on-surface-variant whitespace-pre-line">{selectedClassroom.schedule}</div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Location for offline courses */}
                      {((course.method === 'OFFLINE' || course.method === CourseMethod.OFFLINE) || course.method === CourseMethod.HYBRID) && 
                       selectedClassroom.address && (
                        <div className="flex justify-between items-start">
                          <span className="text-m2-on-surface-variant">Địa điểm:</span>
                          <span className="font-medium text-right">{selectedClassroom.address}</span>
                        </div>
                      )}
                      
                      {/* Online link info */}
                      {((course.method === 'ONLINE' || course.method === CourseMethod.ONLINE) || course.method === CourseMethod.HYBRID) && (
                        <div className="bg-teal-50 border border-teal-200 p-3 rounded-lg">
                          <div className="flex items-center gap-2 text-teal-800">
                            <ComputerDesktopIcon className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              Bạn sẽ được add vào nhóm Zalo của lớp sau khi xác nhận đăng ký thành công
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Error Display */}
                {registrationError && (
                  <div className="mb-4 p-3 bg-m2-error/10 border border-m2-error/30 rounded-md">
                    <div className="flex items-start gap-2">
                      <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      <p className="text-red-700 text-sm">{registrationError}</p>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 justify-end">
                  {/* Show Back button only when coming from class selection */}
                  {showBackButton && (
                    <button
                      onClick={() => {
                        // Clear errors when going back to classroom selection
                        clearRegistrationError();
                        setStep('classroom-selection');
                      }}
                      className="px-4 py-2 text-m2-on-surface-variant border border-m2-outline rounded-md hover:bg-m2-surface-low"
                      disabled={isCreatingRegistration}
                    >
                      Quay lại
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSubmitRegistration}
                    disabled={isCreatingRegistration || invoiceType === 'company'}
                    title={
                      invoiceType === 'company'
                        ? 'Xuất hóa đơn công ty: liên hệ 0983213076 (không đăng ký qua ứng dụng)'
                        : undefined
                    }
                    className="px-6 py-2 bg-m2-primary text-m2-on-primary rounded-md hover:bg-eh-forest-shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isCreatingRegistration && <LoadingSpinner size="sm" />}
                    Xác nhận đăng ký
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 'payment' && currentRegistration?.data?.payment && (() => {
            const reg = currentRegistration.data.registration as Registration & {
              needs_company_invoice?: number | boolean;
            };
            const pay = currentRegistration.data.payment as PaymentInfo;
            const companyInvoiceUi =
              pay.showQr === false ||
              reg.needsCompanyInvoice === true ||
              Number(reg.needs_company_invoice) === 1;
            return companyInvoiceUi ? (
              <CompanyInvoiceGuidance
                registration={reg}
                paymentInfo={pay}
                course={course}
                classroom={selectedClassroom!}
                onClose={onClose}
              />
            ) : (
              <PaymentQRDisplay
                registration={reg}
                paymentInfo={pay}
                course={course}
                classroom={selectedClassroom!}
                onClose={onClose}
              />
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default CourseRegistrationForm;